/** @jsxImportSource @emotion/react */
import { EmptyContent, Header } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  createStyles,
  DateInput,
  Icon,
  Loader,
  Pagination,
  SearchInput,
  Table,
  Typography,
  UtilityButton,
  useTheme,
  Tooltip,
  UtilitySelect,
  Switch,
} from '@quickbit/qb-design-system';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { useGetCases } from 'api/useCasesApi';
import { TruncatedString } from 'components';
import { CaseNotesCell } from 'components/CaseNotesCell';
import { BatchActions } from './BatchActions';
import { BatchDeleteModal } from './BatchDeleteModal';

const headers = [
  { label: 'UUID', cellKey: 'uuid', sort: false },
  { label: 'Created At', cellKey: 'createdAt', sort: false },
  { label: 'Customer Type', cellKey: 'customerType', sort: false },
  { label: 'Title', cellKey: 'title', sort: false },
  { label: 'State', cellKey: 'state', sort: false },
  { label: 'Notes', cellKey: 'notes', sort: false },
];

const CASE_STATE_FILTER_OPTIONS = ['ACTIVE', 'CLOSED'].map((s) => ({
  value: s,
  label: s,
}));

const PAGE_SIZE = 16;

export const CaseList = () => {
  const styles = useStyles();
  const { spacing, palette, sizing } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [customerId, setCustomerId] = useState(
    searchParams.get('customerId') || ''
  );
  const [caseId, setCaseId] = useState(searchParams.get('caseId') || '');
  const [selectedTitles, setSelectedTitles] = useState<string[]>(() => {
    const raw = searchParams.get('titles');
    return raw ? raw.split(',').filter(Boolean) : [];
  });

  const [filterState, setFilterState] = useState(
    searchParams.get('filterState') || 'ACTIVE'
  );
  const [includePostponed, setIncludePostponed] = useState(
    searchParams.get('includePostponed') === 'true'
  );
  // Drill-down filters set when navigating here from the Statistics page.
  const [rule, setRule] = useState(searchParams.get('rule') || '');
  const [reportedToFipo, setReportedToFipo] = useState(
    searchParams.get('reportedToFipo') === 'true'
  );
  const [dateFilterRange, setDateFilterRange] = useState<
    [Date | null, Date | null]
  >(() => {
    const after = searchParams.get('createdAfter');
    const before = searchParams.get('createdBefore');
    return [after ? new Date(after) : null, before ? new Date(before) : null];
  });
  const [page, setPage] = useState(
    Number(searchParams.get('page')) > 0 ? Number(searchParams.get('page')) : 1
  );

  const [debouncedCustomerId, setDebouncedCustomerId] = useState(customerId);
  const [debouncedCaseId, setDebouncedCaseId] = useState(caseId);
  const [debouncedFilterState, setDebouncedfilterState] = useState(filterState);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (customerId) params.customerId = customerId;
    if (caseId) params.caseId = caseId;
    if (selectedTitles.length) params.titles = selectedTitles.join(',');
    if (filterState) params.filterState = filterState;
    if (includePostponed) params.includePostponed = 'true';
    if (rule) params.rule = rule;
    if (reportedToFipo) params.reportedToFipo = 'true';
    if (dateFilterRange[0])
      params.createdAfter = dateFilterRange[0].toISOString();
    if (dateFilterRange[1]) {
      params.createdBefore = dateFilterRange[1].toISOString();
    }
    if (page > 1) params.page = String(page);
    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerId,
    caseId,
    selectedTitles,
    filterState,
    includePostponed,
    rule,
    reportedToFipo,
    dateFilterRange,
    page,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedCustomerId,
    debouncedCaseId,
    selectedTitles,
    debouncedFilterState,
    includePostponed,
    dateFilterRange,
  ]);

  // Debounce search inputs
  useDebounce(
    () => {
      setDebouncedCustomerId(customerId);
      setDebouncedCaseId(caseId);
      setDebouncedfilterState(filterState);
    },
    300,
    [customerId, caseId, filterState]
  );

  const { data, isLoading, isError, refetch } = useGetCases({
    customerId: debouncedCustomerId || undefined,
    caseId: debouncedCaseId || undefined,
    titles: selectedTitles.length ? selectedTitles.join(',') : undefined,
    filterState: debouncedFilterState || undefined,
    includePostponed: includePostponed ? true : undefined,
    rule: rule || undefined,
    reportedToFipo: reportedToFipo || undefined,
    createdAfter: dateFilterRange[0]?.toISOString(),
    createdBefore: dateFilterRange[1]?.toISOString(),
    page,
    size: PAGE_SIZE,
  });

  const titleOptions =
    data?.summary.perTitle.map(({ title, count }) => ({
      value: title,
      label: `${title} (${count})`,
    })) ?? [];
  const selectedTitleOptions = selectedTitles.map((titleValue) => ({
    value: titleValue,
    label:
      titleOptions.find((o) => o.value === titleValue)?.label ?? titleValue,
  }));

  const rows =
    data?.cases.map((caseItem) => ({
      id: caseItem.uuid,
      value: {
        rawId: caseItem.uuid,
        uuid: <TruncatedString text={caseItem.uuid} />,
        createdAt:
          new Date(caseItem.createdAt).toLocaleDateString() +
          ' ' +
          new Date(caseItem.createdAt).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          }), // Format date as needed
        customerType: caseItem.customerType,
        title: caseItem.title,
        state: caseItem.wakeupAt ? (
          <Tooltip title={new Date(caseItem.wakeupAt).toLocaleDateString()}>
            <div>AWAKENED</div>
          </Tooltip>
        ) : (
          caseItem.state
        ),
        notes: <CaseNotesCell notes={caseItem.notes} />,
      },
    })) ?? [];

  if (isError || (!isLoading && !data)) {
    return (
      <EmptyContent
        heading="Oops! Something went wrong."
        body="Error fetching cases"
        action={
          <UtilityButton
            text="Try again"
            variant="primary"
            onClick={() => void refetch()}
          />
        }
      />
    );
  }

  return (
    <>
      <Header>
        <Typography variant="subtitle">Cases</Typography>
      </Header>
      <div
        css={{
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'space-between',
        }}
      >
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, auto) 1fr',
              gap: spacing.xxxs,
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <SearchInput
              placeholder="Customer ID"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            />
            <SearchInput
              placeholder="Case ID"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
            />
          </div>
          <div
            css={{
              marginTop: spacing.xxs,
              marginBottom: spacing.xxs,
              display: 'grid',
              gridTemplateColumns: 'repeat(10, auto) 1fr',
              alignItems: 'center',
              gap: spacing.xxxs,
              textWrap: 'nowrap',
            }}
          >
            <DateInput
              placeholder="All time"
              type="range"
              value={dateFilterRange}
              onChange={(e: [Date | null, Date | null]) => {
                setDateFilterRange(e);
              }}
              withTime
            />
            <UtilitySelect
              label={'Active'}
              listTitle="Active or closed"
              options={CASE_STATE_FILTER_OPTIONS}
              value={
                CASE_STATE_FILTER_OPTIONS.find(
                  (option) => option.value === filterState
                ) ?? null
              }
              onSelect={(data) => {
                if (data === null) {
                  setFilterState('ACTIVE');
                  return;
                }
                if (Array.isArray(data)) return;
                setFilterState(data.value);
              }}
            />
            <UtilitySelect
              multiple
              label={
                selectedTitles.length
                  ? `Titles (${selectedTitles.length})`
                  : 'Titles'
              }
              listTitle="Filter by title"
              options={titleOptions}
              value={selectedTitleOptions}
              onSelect={(data) => {
                const next = Array.isArray(data) ? data : data ? [data] : [];
                setSelectedTitles(next.map((option) => option.value));
              }}
            />
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xxxs,
              }}
            >
              <Switch
                checked={includePostponed}
                onChange={() => setIncludePostponed((prev) => !prev)}
              />
              <Typography variant="bodySmall">Include postponed</Typography>
            </div>
            <UtilityButton
              variant="text"
              text="Reset filters"
              onClick={() => {
                setCustomerId('');
                setCaseId('');
                setSelectedTitles([]);
                setFilterState('ACTIVE');
                setIncludePostponed(false);
                setRule('');
                setReportedToFipo(false);
                setDateFilterRange([null, null]);
                setBatchSelected(new Set());
              }}
            />
            <UtilityButton
              variant="secondary"
              text="Refresh"
              onClick={() => void refetch()}
              disabled={isLoading}
            />
            {isLoading && (
              <Icon
                name="spinner"
                className={{ svg: { color: palette.grey.default } }}
              />
            )}
            {batchSelected.size > 0 && (
              <UtilityButton
                text={`Batch Actions (${batchSelected.size})`}
                dropdown={
                  <Card css={{ minWidth: sizing.medium }}>
                    <BatchActions
                      cases={
                        data?.cases.filter((c) => batchSelected.has(c.uuid)) ??
                        []
                      }
                      onComplete={() => setBatchSelected(new Set())}
                      onDeleteClick={() => setShowDeleteModal(true)}
                    />
                  </Card>
                }
              />
            )}
          </div>
        </div>
        {data?.summary && (
          <div css={{ display: 'flex' }}>
            <Card
              css={{
                padding: spacing.xxs,
                marginBottom: spacing.xxs,
                gap: spacing.xxxs,
                maxHeight: '150px',
                overflowY: 'auto',
              }}
            >
              <div
                css={{
                  display: 'flex',
                  gap: spacing.xxxs,
                  justifyContent: 'space-between',
                  borderBottom: `1px solid ${palette.grey.x300}`,
                  marginBottom: spacing.xxxs,
                }}
              >
                <Typography variant="bodySmallMedium">
                  {`Total active cases:`}
                </Typography>
                <Typography variant="bodySmallMedium">
                  {data.summary.total}
                </Typography>
              </div>
              {data.summary.perTitle.map(({ title: t, count }) => (
                <div
                  key={t}
                  css={{
                    display: 'flex',
                    gap: spacing.xxxs,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="bodySmall">{t}</Typography>
                  <Typography variant="bodySmallMedium">{count}</Typography>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <BatchDeleteModal
          caseIds={Array.from(batchSelected)}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={() => {
            setShowDeleteModal(false);
            setBatchSelected(new Set());
          }}
        />
      )}

      {isLoading && (
        <div css={styles.loadingContainer}>
          <Loader />
        </div>
      )}

      {!isLoading && data?.cases.length === 0 && (
        <EmptyContent heading="No cases" body="There are no active cases" />
      )}

      {!isLoading && data && data.cases.length > 0 && (
        <div css={styles.container}>
          <Table
            headers={headers}
            rows={rows}
            selected={batchSelected}
            updateSelected={setBatchSelected}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            onRowClick={(row: any) => navigate(row.rawId)}
          />
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={data.pagination.total}
            pageSize={PAGE_SIZE}
          />
        </div>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.m,
  },
}));
